import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { adminOrdersApi, adminDriversApi, adminInvoicesApi } from "@/lib/commerce";
import { downloadInvoicePdf } from "@/lib/pdf";
import { formatMoney } from "@/lib/format";
import { toast } from "sonner";

const NEXT_STATES = {
  NEW: ["VERIFIED", "REJECTED"],
  UNDER_REVIEW: ["VERIFIED", "REJECTED"],
  VERIFIED: ["READY_FOR_DELIVERY", "REJECTED", "CANCELLED"],
  PAYMENT_CONFIRMED: ["READY_FOR_DELIVERY", "CANCELLED"],
  READY_FOR_DELIVERY: ["OUT_FOR_DELIVERY", "CANCELLED"],
  OUT_FOR_DELIVERY: ["DELIVERED", "REJECTED"],
  DELIVERED: [],
  REJECTED: [],
  CANCELLED: [],
};

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedDriver, setSelectedDriver] = useState("");
  const [error, setError] = useState("");
  const [invoiceBusy, setInvoiceBusy] = useState(false);

  const load = async () => {
    const o = await adminOrdersApi.get(id);
    setOrder(o);
    setSelectedDriver(o.delivery?.driver?.id || "");
  };

  useEffect(() => {
    load().catch((e) => setError(e.response?.data?.error?.message || "Failed to load."));
    adminDriversApi.list().then(setDrivers).catch(() => setDrivers([]));
  }, [id]);

  const wrap = async (fn, okMsg) => {
    setBusy(true); setError("");
    try {
      await fn();
      await load();
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      const msg = e.response?.data?.error?.message || "Action failed.";
      setError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (!order && !error) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  if (error && !order) return <div className="p-10 text-sm text-destructive">{error}</div>;

  const nextStates = NEXT_STATES[order.status] || [];
  const canVerify = order.status === "NEW" || order.status === "UNDER_REVIEW";
  const canInvoice = !order.invoice && ["VERIFIED", "PAYMENT_CONFIRMED", "READY_FOR_DELIVERY", "OUT_FOR_DELIVERY", "DELIVERED"].includes(order.status);
  const currency = order.totals?.currency || "AED";

  return (
    <div className="min-h-screen bg-background" data-testid="admin-order-root">
      <header className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin › Orders › {order.order_number}</div>
            <div className="text-lg font-semibold">Order {order.order_number}</div>
          </div>
          <Button asChild variant="outline"><Link to="/admin/orders">&larr; All orders</Link></Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 grid gap-6 lg:grid-cols-[1fr_360px]">
        {/* Left column */}
        <div className="space-y-6">
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Status</CardTitle>
                <CardDescription>Current: <span className="font-mono">{order.status}</span></CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3 flex-wrap" data-testid="admin-order-actions">
                {canVerify && (
                  <Button
                    onClick={() => wrap(() => adminOrdersApi.verify(order.id), "Order verified. Customer notified.")}
                    disabled={busy}
                    data-testid="admin-order-verify"
                  >
                    Verify
                  </Button>
                )}
                {nextStates.filter((s) => s !== "REJECTED").map((s) => (
                  <Button
                    key={s} variant="outline"
                    onClick={() => wrap(() => adminOrdersApi.transition(order.id, s), `Order → ${s}`)}
                    disabled={busy}
                    data-testid={`admin-order-transition-${s}`}
                  >
                    → {s.replace(/_/g, " ").toLowerCase()}
                  </Button>
                ))}
              </div>
              {nextStates.includes("REJECTED") && (
                <div className="border-t pt-4 space-y-2">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Reject order</div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Reason (visible to customer)"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      data-testid="admin-order-reject-reason"
                    />
                    <Button
                      variant="destructive"
                      disabled={busy || !rejectReason.trim()}
                      onClick={() => wrap(
                        () => adminOrdersApi.reject(order.id, rejectReason.trim()),
                        "Order rejected. Stock restored.",
                      )}
                      data-testid="admin-order-reject"
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Items</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground border-b">
                  <tr><th className="pb-2">Item</th><th className="pb-2 text-center">Qty</th><th className="pb-2 text-right">Total</th></tr>
                </thead>
                <tbody>
                  {order.items.map((it) => (
                    <tr key={it.sku} className="border-b last:border-0">
                      <td className="py-2">
                        <div>{it.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{it.sku}</div>
                      </td>
                      <td className="py-2 text-center">{it.qty}</td>
                      <td className="py-2 text-right">{formatMoney({ amount_minor: it.line_total_minor, currency })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Separator />
              <div className="text-sm space-y-1">
                <Row label="Subtotal" value={formatMoney({ amount_minor: order.totals.subtotal_minor, currency })} />
                <Row label="VAT" value={formatMoney({ amount_minor: order.totals.tax_minor, currency })} />
                <Row label="Delivery" value={formatMoney({ amount_minor: order.totals.delivery_fee_minor || 0, currency })} />
                <Row label="Grand total" value={formatMoney({ amount_minor: order.totals.grand_total_minor, currency })} bold />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Status history</CardTitle></CardHeader>
            <CardContent>
              <ol className="space-y-2 text-sm" data-testid="admin-order-history">
                {(order.status_history || []).map((h, idx) => (
                  <li key={idx} className="flex gap-3">
                    <div className="w-1 rounded bg-slate-300 self-stretch mt-1" />
                    <div className="flex-1">
                      <div className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString()}</div>
                      <div><span className="font-mono">{h.from || "∅"} → {h.to}</span></div>
                      {h.note && <div className="text-xs text-muted-foreground">{h.note}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <aside className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Customer</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1">
              <div className="font-medium">{order.customer.name}</div>
              <div className="text-muted-foreground">{order.customer.email}</div>
              <div className="text-muted-foreground">{order.customer.phone}</div>
              <a
                className="inline-block text-xs mt-2 text-emerald-700 hover:underline"
                href={`https://wa.me/${(order.customer.phone || "").replace(/[^0-9]/g, "")}?text=Hi%20${encodeURIComponent(order.customer.name)}%20about%20order%20${order.order_number}`}
                target="_blank" rel="noreferrer"
                data-testid="admin-order-whatsapp"
              >
                WhatsApp customer
              </a>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Delivery</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              <div>{order.delivery_address.building}, {order.delivery_address.area}</div>
              <div className="text-muted-foreground">{order.delivery_address.emirate}, UAE</div>
              {order.delivery_address.instructions && (
                <div className="text-xs text-muted-foreground">Note: {order.delivery_address.instructions}</div>
              )}
              <Separator />
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Driver</div>
              {order.delivery?.driver ? (
                <div>
                  <div className="font-medium">{order.delivery.driver.name}</div>
                  <div className="text-xs text-muted-foreground">{order.delivery.driver.phone}</div>
                  <div className="text-xs text-muted-foreground">Status: {order.delivery.status}</div>
                </div>
              ) : (
                <div className="text-muted-foreground text-xs">No driver assigned yet.</div>
              )}
              {drivers.length > 0 && order.status !== "DELIVERED" && order.status !== "REJECTED" && order.status !== "CANCELLED" && (
                <div className="pt-2 space-y-2">
                  <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                    <SelectTrigger data-testid="admin-order-driver-select"><SelectValue placeholder="Assign driver" /></SelectTrigger>
                    <SelectContent>
                      {drivers.filter((d) => d.is_active).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name} · {d.phone}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    className="w-full" size="sm"
                    disabled={busy || !selectedDriver}
                    onClick={() => wrap(() => adminOrdersApi.assignDelivery(order.id, selectedDriver), "Driver assigned.")}
                    data-testid="admin-order-assign-driver"
                  >
                    Assign driver
                  </Button>
                </div>
              )}
              {drivers.length === 0 && (
                <div className="text-xs text-muted-foreground">
                  <Link to="/admin/drivers" className="underline">Add drivers</Link> first.
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Payment</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1">
              <div>Method: {order.payment.method.replace(/_/g, " ")}</div>
              <div>Status: <span className="font-mono">{order.payment.status}</span></div>
              {order.payment.method === "cash_on_delivery" && order.payment.status !== "PAID" && ["VERIFIED", "READY_FOR_DELIVERY", "OUT_FOR_DELIVERY", "DELIVERED"].includes(order.status) && (
                <Button size="sm" variant="outline" className="mt-2" onClick={() => wrap(() => adminOrdersApi.confirmPayment(order.id, { note: "cash received" }), "Payment marked as received.")} data-testid="admin-order-payment-confirm">
                  Mark cash received
                </Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Tax invoice</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              {order.invoice ? (
                <>
                  <div className="font-mono text-xs">{order.invoice.invoice_number}</div>
                  <div className="text-xs text-muted-foreground">Issued: {new Date(order.invoice.issued_at).toLocaleString()}</div>
                  <Button
                    size="sm" variant="outline" className="w-full"
                    onClick={() => downloadInvoicePdf(order.invoice.id, order.invoice.invoice_number)}
                    data-testid="admin-order-view-invoice"
                  >
                    Download PDF
                  </Button>
                </>
              ) : canInvoice ? (
                <Button
                  size="sm" className="w-full"
                  disabled={invoiceBusy}
                  onClick={async () => {
                    setInvoiceBusy(true);
                    try {
                      const inv = await adminInvoicesApi.createFromOrder(order.id);
                      toast.success(`Invoice ${inv.invoice_number} issued.`);
                      await load();
                    } catch (e) {
                      toast.error(e.response?.data?.error?.message || "Invoice creation failed.");
                    } finally { setInvoiceBusy(false); }
                  }}
                  data-testid="admin-order-issue-invoice"
                >
                  Issue tax invoice
                </Button>
              ) : (
                <div className="text-xs text-muted-foreground">
                  Invoice can be issued after the order is verified.
                </div>
              )}
            </CardContent>
          </Card>
        </aside>
      </main>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className={bold ? "font-semibold" : ""}>{label}</span>
      <span className={bold ? "font-semibold" : ""}>{value}</span>
    </div>
  );
}
